import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import { 
  CheckCircle as CheckIcon, 
  Phone as PhoneIcon, 
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon 
} from '@mui/icons-material';

import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';
import { bookingApi } from '../services/api';

const AppointmentConfirmation = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appointmentId = searchParams.get('id');

  // Helper function for translating strings with parameters
  const translate = (key, params = {}) => {
    let text = t(key);
    Object.keys(params).forEach(param => {
      text = text.replace(`{${param}}`, params[param]);
    });
    return text;
  };
  
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  useEffect(() => {
    console.log('AppointmentConfirmation mounted with ID:', appointmentId);
    if (appointmentId) {
      loadAppointment();
    } else {
      setError(t('confirmation.noIdProvided'));
      setLoading(false);
    }
  }, [appointmentId]);

  // Handle auto-confirmation from email link
  useEffect(() => {
    const confirmParam = searchParams.get('confirm');
    if (appointmentId && confirmParam === 'true' && appointment && appointment.status === 'pending') {
      handleAutoConfirmation();
    }
  }, [appointmentId, appointment]);

  const loadAppointment = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Loading appointment with ID:', appointmentId);
      const data = await bookingApi.getAppointment(appointmentId);
      console.log('Received appointment data:', data);
      setAppointment(data);
    } catch (err) {
      console.error('Error loading appointment:', err);
      setError(translate('confirmation.loadError', { error: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleAutoConfirmation = async () => {
    setConfirming(true);
    setConfirmationMessage('');
    
    try {
      console.log('Auto-confirming appointment:', appointmentId);
      const result = await bookingApi.confirmAppointment(appointmentId);
      console.log('Confirmation result:', result);
      
      if (result.success) {
        if (result.status === 'already_confirmed') {
          setConfirmationMessage(t('confirmation.alreadyConfirmed'));
        } else {
          setConfirmationMessage(t('confirmation.confirmationSuccess'));
        }
        // Reload appointment to get updated status
        await loadAppointment();
      }
    } catch (err) {
      console.error('Error confirming appointment:', err);
      setError(translate('confirmation.confirmationError', { error: err.message }));
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelAppointment = async () => {
    setCancelling(true);
    try {
      await bookingApi.cancelAppointment(appointmentId, cancelReason);
      setCancelDialog(false);
      setCancelReason('');
      // Refresh the page to show updated status
      window.location.reload();
    } catch (err) {
      setError(translate('confirmation.cancelError', { error: err.message }));
      console.error('Error canceling appointment:', err);
    } finally {
      setCancelling(false);
    }
  };

  const getTimeUntilAppointment = () => {
    if (!appointment) return 0;
    const appointmentDateTime = dayjs(`${appointment.date} ${appointment.time}`);
    const now = dayjs();
    return appointmentDateTime.diff(now, 'hour');
  };

  const canModifyAppointment = () => {
    if (!appointment || appointment.status === 'cancelled') return false;
    return getTimeUntilAppointment() > 24;
  };

  const isAppointmentSoon = () => {
    if (!appointment || appointment.status === 'cancelled') return false;
    const hoursUntil = getTimeUntilAppointment();
    return hoursUntil <= 24 && hoursUntil > 0;
  };

  const handleReschedule = () => {
    // Navigate to schedule page with current appointment data for modification
    navigate(`/schedule?reschedule=${appointmentId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>{t('confirmation.loadingDetails')}</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button component={Link} to="/" variant="contained">
            {t('confirmation.backToHome')}
          </Button>
        </Box>
      </Container>
    );
  }

  if (!appointment) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning">
          {t('confirmation.notFound')}
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button component={Link} to="/" variant="contained">
            {t('confirmation.backToHome')}
          </Button>
        </Box>
      </Container>
    );
  }

  const hoursUntilAppointment = getTimeUntilAppointment();
  const appointmentDate = dayjs(appointment.date);
  const isUpcoming = hoursUntilAppointment > 0;

  return (
    <Box component="main" sx={{ flexGrow: 1 }}>
        <Header />
        
      <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Confirmation Loading/Messages */}
      {confirming && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography>{t('confirmation.confirmingAppointment')}</Typography>
          </Box>
        </Alert>
      )}

      {confirmationMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography>{confirmationMessage}</Typography>
        </Alert>
      )}

      {/* Status Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        {appointment.status === 'cancelled' ? (
          <>
            <WarningIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('confirmation.cancelled')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {t('confirmation.cancelledText')}
            </Typography>
          </>
        ) : appointment.status === 'pending' ? (
          <>
            <ScheduleIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('schedule.confirmationRequired')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {t('confirmation.pendingText')}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                {t('confirmation.pendingInstructions')}
              </Typography>
            </Alert>
          </>
        ) : (
          <>
            <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('confirmation.title')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {t('confirmation.confirmedText')}
            </Typography>
            
            {/* Time-based status chip */}
            {isUpcoming && (
              <Box sx={{ mt: 2 }}>
                {hoursUntilAppointment > 24 ? (
                  <Chip 
                    icon={<ScheduleIcon />}
                    label={translate('confirmation.daysUntil', { days: Math.floor(hoursUntilAppointment / 24) })}
                    color="info"
                    variant="outlined"
                  />
                ) : hoursUntilAppointment > 0 ? (
                  <Chip 
                    icon={<WarningIcon />}
                    label={translate('confirmation.hoursUntil', { hours: hoursUntilAppointment })}
                    color="warning"
                    variant="outlined"
                  />
                ) : (
                  <Chip 
                    label={t('confirmation.appointmentPassed')}
                    color="default"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Appointment Details */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {t('confirmation.appointmentDetails')}
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('confirmation.dateTime')}
                </Typography>
                <Typography variant="h6">
                  {appointmentDate.format('dddd, MMMM D, YYYY')}
                </Typography>
                <Typography variant="h6">
                  {appointment.time}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('schedule.appointmentType')}:
                </Typography>
                <Typography variant="h6">
                  { appointment.appointmentType }
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('confirmation.clientInfo')}
                </Typography>
                <Typography variant="body1">
                  {appointment.firstName} {appointment.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {appointment.email}
                </Typography>
                {appointment.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {appointment.phone}
                  </Typography>
                )}
                <Chip 
                  label={appointment.status === 'confirmed' ? t('confirmation.confirmed') : appointment.status} 
                  color={appointment.status === 'confirmed' ? 'success' : 'default'}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          {appointment.message && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('confirmation.messageNotes')}
                  </Typography>
                  <Typography variant="body1">
                    {appointment.message}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Action Buttons */}
      {appointment.status === 'confirmed' && isUpcoming && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
            {t('confirmation.needChanges')}
          </Typography>
          
          {canModifyAppointment() ? (
            // More than 24 hours - allow modifications
            <Box>
              
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                
                
                <Button
                  onClick={() => setCancelDialog(true)}
                  variant="outlined"
                  color="error"
                  size="large"
                >
                  {t('schedule.cancelAppointment')}
                </Button>
              </Box>
            </Box>
          ) : isAppointmentSoon() ? (
            // Less than 24 hours - show contact info
            <Box>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t('confirmation.contactWithin24h')}
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6">{t('confirmation.email')}</Typography>
                      <Typography variant="body1" color="primary">
                        <a href="mailto:blau.body@gmail.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                          blau.body@gmail.com
                        </a>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <PhoneIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6">{t('confirmation.phone')}</Typography>
                      <Typography variant="body1" color="primary">
                        <a href="tel:+436991107435" style={{ textDecoration: 'none', color: 'inherit' }}>
                          +43 699 11074356
                        </a>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </Paper>
      )}

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button component={Link} to="/" variant="contained" size="large">
          {t('confirmation.backToHome')}
        </Button>
      </Box>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('confirmation.cancelDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {translate('confirmation.cancelDialogText', { 
              date: appointmentDate.format('MMMM D, YYYY'), 
              time: appointment.time 
            })}
          </Typography>
          <TextField
            fullWidth
            label={t('confirmation.cancelReason')}
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>
            {t('confirmation.keepAppointment')}
          </Button>
          <Button 
            onClick={handleCancelAppointment} 
            variant="contained" 
            color="error"
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={20} /> : t('schedule.cancelAppointment')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    <Footer />
    </Box>
  );
};

export default AppointmentConfirmation; 